/**
 * Calculation and aggregation utilities
 */

import { startOfDay, endOfDay, differenceInMilliseconds } from 'date-fns';
import type { FeedEntry, SleepEntry, DiaperEntry, TodaySummary } from '../types';

/**
 * Get entries for a specific date
 */
function getEntriesForDate<T extends { timestamp: Date }>(entries: T[], date: Date): T[] {
  const start = startOfDay(date);
  const end = endOfDay(date);
  return entries.filter(e => e.timestamp >= start && e.timestamp <= end);
}

function getSleepsForDate(sleeps: SleepEntry[], date: Date): SleepEntry[] {
  const start = startOfDay(date);
  const end = endOfDay(date);
  // Include sleeps that start on this date
  return sleeps.filter(s => s.startTime >= start && s.startTime <= end);
}

/**
 * Calculate sleep duration in hours
 */
export function calculateSleepDuration(sleep: SleepEntry): number {
  const ms = differenceInMilliseconds(sleep.endTime, sleep.startTime);
  return ms / (1000 * 60 * 60); // Convert to hours
}

/**
 * Calculate total sleep for a date
 */
export function getTotalSleepHours(sleeps: SleepEntry[], date: Date): number {
  const daySleeps = getSleepsForDate(sleeps, date);
  const totalHours = daySleeps.reduce((sum, sleep) => {
    return sum + calculateSleepDuration(sleep);
  }, 0);
  return Math.round(totalHours * 10) / 10; // Round to 1 decimal
}


/**
 * Get today's summary
 */
export function getTodaySummary(
  feeds: FeedEntry[],
  sleeps: SleepEntry[],
  diapers: DiaperEntry[]
): TodaySummary {
  const today = new Date();

  // Get all entries for today
  const todayFeeds = getEntriesForDate(feeds, today);
  const todaySleeps = getSleepsForDate(sleeps, today);
  const todayDiapers = getEntriesForDate(diapers, today);

  // Find last activity
  const allActivities = [
    ...todayFeeds.map(f => ({ type: 'feed' as const, timestamp: f.timestamp })),
    ...todaySleeps.map(s => ({ type: 'sleep' as const, timestamp: s.startTime })),
    ...todayDiapers.map(d => ({ type: 'diaper' as const, timestamp: d.timestamp })),
  ];

  allActivities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  const lastActivity = allActivities[0];

  return {
    feedCount: todayFeeds.length,
    totalSleepHours: getTotalSleepHours(sleeps, today),
    diaperCount: todayDiapers.length,
    lastActivity: lastActivity
      ? { type: lastActivity.type, timestamp: lastActivity.timestamp }
      : undefined,
  };
}

